const destinations = [
  {
    name: "Saudi Arabia & Qatar",
    context: "LNG project assignments, petrochemical turnarounds, and energy technology licensing negotiations from Lake Charles-based companies",
    urgency: "Plant turnaround schedules are fixed — engineers must depart on contract start dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Netherlands & Germany",
    context: "Chemical process engineering visits, equipment vendor training, and offshore technology transfer for Calcasieu industrial corridor",
    urgency: "Technical training programs often confirmed 2–3 weeks before course start",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Macau & Singapore",
    context: "Casino industry executive conferences, hospitality training programs, and gaming technology vendor meetings",
    urgency: "Industry conference registrations confirmed 3–4 weeks before departure",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Bahamas & Caribbean",
    context: "Galveston and New Orleans cruise sailings, family beach vacations, spring break trips from Southwest Louisiana",
    urgency: "Last-minute cruise deals and holiday bookings come together in 2–4 weeks",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom",
    context: "Energy sector offshore technology, London financial and gaming industry connections, North Sea project work",
    urgency: "UK offshore projects have non-negotiable offshore crew change dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "South Korea & Japan",
    context: "LNG technology sourcing, shipbuilding industry visits, and energy equipment procurement for Calcasieu LNG terminals",
    urgency: "Vendor and procurement travel often confirmed with 2–3 weeks notice",
    icon: "ri-map-pin-2-line",
  },
];

const commonSituations = [
  {
    stat: "78%",
    label: "of Lake Charles expedited requests are industrial sector or discovered within 3 weeks of travel",
  },
  {
    stat: "10–12 Days",
    label: "average notice Lake Charles petrochemical engineers receive before an international assignment",
  },
  {
    stat: "100 Miles",
    label: "to Port of Galveston — where many Lake Charles families board Caribbean and Bahamas cruises",
  },
  {
    stat: "2 Weeks",
    label: "minimum lead time needed for expedited processing when a country requires a visa alongside your passport",
  },
];

export default function LakeCharlesDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Lake Charles Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Southwest Louisiana clients. Understanding local travel behavior is part of why our document review catches issues before they derail your plant assignment, cruise, or industry conference.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Louisiana Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Louisiana with the same expedited passport processing and document review expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/new-orleans" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                New Orleans <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/lafayette" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Lafayette <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/shreveport" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Shreveport <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}